import {
  faAlignJustify,
  faCalendar,
  faCheck,
  faUserPlus
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Avatar from 'components/common/Avatar';
import Flex from 'components/common/Flex';
import DeadlineTaskEditModal from 'components/modals/DeadlineTaskEditModal';
import useApi from 'helpers/api';
import { formatDeadline } from 'helpers/utils';
import { useAuthenticatedUser } from 'hooks/useAuthenticatedUser';
import { useEffect, useRef, useState } from 'react';
import {
  Button,
  Card,
  Dropdown,
  Form,
  OverlayTrigger,
  Tooltip
} from 'react-bootstrap';
import AgentPhotoService from 'services/AgentPhoto.service';
import TicketTasksService from 'services/TicketTasks.service';
import './TaskCard.css';

/**
 * NewTaskCard component for creating and managing tasks.
 *
 * @param {Object} props - Component properties.
 * @param {number} props.ticketId - The ID of the ticket associated with the task.
 * @param {Function} props.onCancel - Callback function triggered when the task creation is canceled.
 * @param {Function} props.onTaskAdded - Callback function triggered when a new task is successfully added.
 * @param {boolean} [props.isGroupTitle=false] - Determines if the card is rendered as a group title.
 *        When true, the card displays a larger title input and hides task-specific actions.
 */
const NewTaskCard = ({
  ticketId,
  onCancel,
  onTaskAdded,
  isGroupTitle = false
}) => {
  const api = useApi();
  const titleInputRef = useRef(null);
  const deadlineInputRef = useRef(null);
  const agentSelectRef = useRef(null);
  const { authenticatedUser } = useAuthenticatedUser();
  const [agents, setAgents] = useState([]);
  const [agentPhoto, setAgentPhoto] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);

  // Estados para manipulação de visualização de modais
  const [showDeadlineModal, setShowDeadlineModal] = useState(false);

  // Estados para controlar quais campos estão sendo editados
  const [showDeadlineInput, setShowDeadlineInput] = useState(false);
  const [showAgentInput, setShowAgentInput] = useState(false);
  // Set default values for new task
  const getTodayDatetime = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Set time to 00:00:00.000
    return new Date(today.getTime() - today.getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 16); // Format as YYYY-MM-DDT00:00 in local timezone
  };

  const cardRef = useRef(null);

  const initialState = {
    title: '',
    deadline: getTodayDatetime(),
    agent: { id: '' },
    description: '',
    status: false,
    isGroupTitle: false
  };

  const [taskData, setTaskData] = useState(initialState);

  // Focus on title input when component mounts
  useEffect(() => {
    if (titleInputRef.current) {
      titleInputRef.current.focus();
    }

    if (authenticatedUser?.id) {
      setCurrentUserId(authenticatedUser.id);
    }
  }, [authenticatedUser]);

  useEffect(() => {
    setTaskData(prev => ({
      ...prev,
      isGroupTitle: isGroupTitle
    }));
  }, [isGroupTitle]);

  // Effect for focusing deadline input when it becomes visible
  useEffect(() => {
    if (showDeadlineInput && deadlineInputRef.current) {
      deadlineInputRef.current.focus();
    }
  }, [showDeadlineInput]);

  // Effect for focusing agent input when it becomes visible
  useEffect(() => {
    if (showAgentInput && agentSelectRef.current) {
      agentSelectRef.current.focus();
    }
  }, [showAgentInput]);

  useEffect(() => {
    getAgents();
    if (currentUserId) {
      fetchAgentPhoto(currentUserId);
      // Set current user as default agent
      setTaskData(prev => ({
        ...prev,
        agent: { id: currentUserId }
      }));
    }
  }, [currentUserId]);

  useEffect(() => {
    if (taskData.agent?.id) {
      fetchAgentPhoto(taskData.agent.id);
    }
  }, [taskData.agent?.id]);

  const getAgents = async () => {
    const response = await api.agents.listSummaryData();
    setAgents(response);
  };

  const fetchAgentPhoto = async agentId => {
    if (!agentId) return;

    try {
      const request = await AgentPhotoService.find(agentId);
      if (request.ok) {
        const response = await request.json();
        setAgentPhoto(response.url);
      } else {
        setAgentPhoto(null); // Reset photo if not found
      }
    } catch (error) {
      console.log(`Foto não encontrada para o agente ${agentId}`);
    }
  };

  const handleInputChange = (field, value) => {
    setTaskData(prev => ({
      ...prev,
      [field]: field === 'agent' ? { id: parseInt(value) } : value
    }));
  };

  const handleSubmit = async () => {
    if (!taskData.title.trim()) {
      return; // Don't submit if title is empty
    }

    setIsLoading(true);
    try {
      await TicketTasksService.create(ticketId, taskData);
      startNewTask();
      onTaskAdded();
    } catch (error) {
      console.error('Erro ao criar tarefa:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = event => {
      if (showDeadlineModal) return;

      if (cardRef.current && !cardRef.current.contains(event.target)) {
        onCancel();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onCancel, showDeadlineModal]);

  const handleKeyDown = e => {
    if (e.key === 'Enter') {
      if (showDeadlineInput) {
        setShowDeadlineInput(false);
      } else if (showAgentInput) {
        setShowAgentInput(false);
      } else {
        handleSubmit();
      }
    } else if (e.key === 'Escape') {
      if (showDeadlineInput) {
        setShowDeadlineInput(false);
      } else if (showAgentInput) {
        setShowAgentInput(false);
      } else {
        onCancel();
      }
    }
  };

  const startNewTask = () => {
    setTaskData({
      ...initialState,
      isGroupTitle: isGroupTitle,
      agent: { id: currentUserId }
    });
    setShowDeadlineInput(false);
    setShowAgentInput(false);
    titleInputRef.current.focus();
  };

  // Função para obter o nome do agente atual
  const getCurrentAgentName = () => {
    if (!taskData.agent?.id || !agents) return '';
    const agent = agents.find(a => a.id === taskData.agent.id);
    return agent ? `${agent.firstName} ${agent.lastName}` : '';
  };

  // Função auxiliar para determinar a cor da deadline
  const getDeadlineColor = deadlineDate => {
    if (!deadlineDate) return 'text-secondary';

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const deadline = new Date(deadlineDate);
    deadline.setHours(0, 0, 0, 0);

    if (deadline < today) {
      return 'text-danger';
    } else if (deadline.getTime() === today.getTime()) {
      return 'text-success';
    } else {
      return 'text-info';
    }
  };

  return (
    <>
      <DeadlineTaskEditModal
        show={showDeadlineModal}
        close={() => setShowDeadlineModal(false)}
        taskData={taskData}
        setTaskData={setTaskData}
      />
      <Card
        ref={cardRef}
        role="button"
        className={`border-0 shadow-sm mb-2`}
        style={{
          borderRadius: '8px'
        }}
      >
        <Card.Body
          as={Flex}
          justifyContent="between"
          alignItems="center"
          className="py-2 px-3"
          style={{ minHeight: '64px' }}
        >
          <Flex alignItems="center" className={'flex-1'}>
            {!isGroupTitle && (
              <Button
                variant="link"
                size="sm"
                className="me-2"
                style={{
                  width: '24px',
                  height: '24px',
                  padding: '0',
                  color: '#e0e0e0',
                  border: '1px solid #e0e0e0',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <FontAwesomeIcon icon={faCheck} className="fs-11" />
              </Button>
            )}

            {/* Input de título */}
            <Form.Control
              ref={titleInputRef}
              value={taskData.title}
              onChange={e => handleInputChange('title', e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Digite o título da tarefa..."
              size={isGroupTitle ? 'lg' : 'sm'}
              className="border-0 shadow-none"
            />
          </Flex>

          {!isGroupTitle && (
            <Flex alignItems="center" className="task-actions">
              {/* Deadline - mostra o ícone ou o input */}
              <Button
                variant="link"
                className={`mx-3 p-0 fs-10 text-decoration-none ${getDeadlineColor(
                  taskData.deadline
                )}`}
                onClick={() => setShowDeadlineModal(true)}
              >
                {formatDeadline(taskData.deadline) || (
                  <FontAwesomeIcon icon={faCalendar} />
                )}
              </Button>

              {/* Tooltip para descrição */}
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip id="tooltip-description-new">
                    {taskData.description
                      ? taskData.description
                      : 'Descrição da tarefa'}
                  </Tooltip>
                }
              >
                <Button variant="link" className="mx-3 p-0 text-secondary">
                  <FontAwesomeIcon icon={faAlignJustify} />
                </Button>
              </OverlayTrigger>

              {/* Avatar/seleção de agente */}
              {showAgentInput ? (
                <Form.Select
                  ref={agentSelectRef}
                  size="sm"
                  value={taskData.agent.id}
                  onChange={e => handleInputChange('agent', e.target.value)}
                  onKeyDown={handleKeyDown}
                  onBlur={() => setShowAgentInput(false)}
                  className="mx-3"
                  style={{ width: '150px' }}
                >
                  <option value={null}>Selecione um agente</option>
                  {agents?.map(agent => (
                    <option key={agent.id} value={agent.id}>
                      {agent.firstName} {agent.lastName}
                    </option>
                  ))}
                </Form.Select>
              ) : taskData.agent?.id ? (
                <div
                  onClick={() => setShowAgentInput(true)}
                  role="button"
                  className="mx-3"
                >
                  <Avatar src={agentPhoto} name={getCurrentAgentName()} />
                </div>
              ) : (
                <Button
                  variant="outline-secondary"
                  size="sm"
                  className="mx-3 rounded-circle"
                  style={{
                    width: '30px',
                    height: '30px',
                    padding: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  onClick={() => setShowAgentInput(true)}
                >
                  <FontAwesomeIcon icon={faUserPlus} className="fs-11" />
                </Button>
              )}

              {/* Botão de contexto com dropdown */}
              <Dropdown className="btn-reveal-trigger opacity-100" align="end">
                <Dropdown.Toggle
                  as={Button}
                  variant="link"
                  className="p-0 text-secondary ms-2"
                  disabled
                >
                  <FontAwesomeIcon icon="ellipsis-h" />
                </Dropdown.Toggle>

                <Dropdown.Menu>
                  <Dropdown.Item
                    onClick={() => {
                      setTask(task);
                      setShowEditModal(true);
                    }}
                  >
                    Editar
                  </Dropdown.Item>
                  <Dropdown.Item
                    className="text-danger"
                    onClick={() => {
                      setTaskIdToDelete(task.id);
                      setShowDeleteConfirmModal(true);
                    }}
                  >
                    Excluir
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </Flex>
          )}
        </Card.Body>
      </Card>
    </>
  );
};

export default NewTaskCard;
